import React, { useState, useEffect, useCallback } from "react";
import { ref, onValue, off, set as fbSet } from "firebase/database";
import { db } from "../../firebase/config";
import { useProfileStore } from "../../store/profileStore";

function getMondayOfWeek(d: Date): string {
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const mon = new Date(d);
  mon.setDate(d.getDate() + diff);
  return mon.toISOString().slice(0, 10);
}
function addDays(iso: string, n: number): string {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function dateRange(start: string, end: string): string[] {
  const dates: string[] = [];
  const cur = new Date(start + "T00:00:00");
  const last = new Date(end + "T00:00:00");
  while (cur <= last) {
    dates.push(cur.toISOString().slice(0, 10));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}
function dayName(d: string) {
  return new Date(d + "T00:00:00").toLocaleDateString("en-GB", {
    weekday: "long",
  });
}
function displayDate(d: string) {
  return new Date(d + "T00:00:00").toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

// Always start from Mon 6 Apr 2026 as the baseline, then advance by whole weeks
function getCurrentWeekMonday(): string {
  const baseline = new Date("2026-04-06T00:00:00");
  const now = new Date();
  if (now < baseline) return "2026-04-06";
  const diffMs = now.getTime() - baseline.getTime();
  const diffWeeks = Math.floor(diffMs / (7 * 24 * 60 * 60 * 1000));
  return addDays("2026-04-06", diffWeeks * 7);
}

interface SlotRaw {
  driverId: string | null;
  sidemanId: string | null;
  sideman2Id: string | null;
  timeDuration: string;
  crewOff: Array<{ id: string; label: "D" | "S" }>;
  routeLabel?: string;
}

const DashboardTab: React.FC = () => {
  const profiles = useProfileStore((s) => s.profiles);

  const [weekMonday, setWeekMonday] = useState("2026-04-06");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [useCustom, setUseCustom] = useState(false);
  const [search, setSearch] = useState("");
  const [slotsByDate, setSlotsByDate] = useState<Record<string, SlotRaw[]>>({});
  const [extraPay, setExtraPay] = useState<Record<string, string>>({});
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [openReason, setOpenReason] = useState<string | null>(null);

  // ── Persist extra pay + reasons keyed by weekMonday ──
  const payKey = "dashboard_pay/" + weekMonday;
  const reasonKey = "dashboard_reasons/" + weekMonday;

  useEffect(() => {
    const r1 = ref(db, payKey);
    const r2 = ref(db, reasonKey);
    const u1 = onValue(r1, (snap) => {
      if (snap.val()) setExtraPay(snap.val());
      else setExtraPay({});
    });
    const u2 = onValue(r2, (snap) => {
      if (snap.val()) setReasons(snap.val());
      else setReasons({});
    });
    return () => {
      u1();
      u2();
    };
  }, [weekMonday]);

  const saveExtraPay = (key: string, val: string) => {
    const next = { ...extraPay, [key]: val };
    setExtraPay(next);
    fbSet(ref(db, payKey), next).catch((e) => console.warn("pay save", e));
  };
  const saveReason = (key: string, val: string) => {
    const next = { ...reasons, [key]: val };
    setReasons(next);
    fbSet(ref(db, reasonKey), next).catch((e) =>
      console.warn("reason save", e),
    );
  };

  const startDate = useCustom && customStart ? customStart : weekMonday;
  const endDate = useCustom && customEnd ? customEnd : addDays(weekMonday, 6);
  const dates =
    useCustom && customStart && customEnd
      ? dateRange(customStart, customEnd)
      : Array.from({ length: 7 }, (_, i) => addDays(weekMonday, i));

  // ── Auto-advance week every Monday at midnight UK time ──
  useEffect(() => {
    const checkWeek = () => {
      const newMon = getCurrentWeekMonday();
      setWeekMonday((prev) => (prev !== newMon ? newMon : prev));
    };
    const now = new Date();
    const nextMon = new Date(now);
    nextMon.setDate(now.getDate() + ((1 + 7 - now.getDay()) % 7 || 7));
    nextMon.setHours(0, 0, 0, 0);
    const msUntil = nextMon.getTime() - now.getTime();
    const t = setTimeout(() => {
      checkWeek();
      setInterval(checkWeek, 7 * 24 * 60 * 60 * 1000);
    }, msUntil);
    return () => clearTimeout(t);
  }, []);

  // ── Live sync rb_slots for every date in range ──
  useEffect(() => {
    setSlotsByDate({});
    const unsubs: (() => void)[] = [];
    dates.forEach((date) => {
      const r = ref(db, "rb_slots/" + date);
      const unsub = onValue(r, (snap) => {
        const raw = snap.val();
        setSlotsByDate((prev) => ({
          ...prev,
          [date]:
            raw && Array.isArray(raw)
              ? raw.map((s: any) => ({
                  driverId: s.driverId ?? null,
                  sidemanId: s.sidemanId ?? null,
                  sideman2Id: s.sideman2Id ?? null,
                  timeDuration: s.timeDuration ?? "",
                  crewOff: s.crewOff ?? [],
                  routeLabel: s.routeLabel ?? undefined,
                }))
              : [],
        }));
      });
      unsubs.push(() => unsub());
    });
    return () => unsubs.forEach((fn) => fn());
  }, [startDate, endDate]);

  const getName = (id: string | null) => {
    if (!id) return "—";
    const p = profiles.find((x) => x.id === id);
    return p ? `${p.firstName}${p.lastName ? " " + p.lastName : ""}` : "—";
  };

  const getRow = (
    profileId: string,
    role: "driver" | "sideman",
    date: string,
  ) => {
    const slots = slotsByDate[date] || [];
    for (let i = 0; i < slots.length; i++) {
      if ((slots[i].crewOff || []).some((x) => x.id === profileId))
        return { off: true, routeNo: "", partner: "", duration: "" };
    }
    for (let i = 0; i < slots.length; i++) {
      const s = slots[i];
      let mine = false;
      if (role === "driver") mine = s.driverId === profileId;
      else mine = s.sidemanId === profileId || s.sideman2Id === profileId;
      if (mine) {
        const partnerId = role === "driver" ? s.sidemanId : s.driverId;
        const partner2Id = role === "driver" ? s.sideman2Id : null;
        return {
          off: false,
          routeNo: slots[i].routeLabel?.trim() || "Route " + (i + 1),
          partner: getName(partnerId),
          partner2: partner2Id ? getName(partner2Id) : "",
          duration: s.timeDuration || "",
        };
      }
    }
    return { off: false, routeNo: "", partner: "", duration: "", empty: true };
  };

  // ── CSV download ──
  const downloadCSV = useCallback(() => {
    const rows: string[][] = [
      [
        "Role",
        "Name",
        "User ID",
        "Day",
        "Date",
        "Route No",
        "Partner",
        "Sideman 2",
        "Time Duration",
        "Extra Pay",
        "Reason for Extra Pay",
      ],
    ];
    (["driver", "sideman"] as const).forEach((role) => {
      profiles
        .filter((p) => p.role === role)
        .forEach((p) => {
          const name = `${p.firstName}${p.lastName ? " " + p.lastName : ""}`;
          const userId = (p as any).age || "—";
          dates.forEach((date) => {
            const row = getRow(p.id, role, date) as any;
            rows.push([
              role.toUpperCase(),
              name,
              userId,
              dayName(date),
              displayDate(date),
              row.off ? "—" : row.routeNo || "—",
              row.off ? "—" : row.partner || "—",
              row.off ? "—" : (row as any).partner2 || "—",
              row.off ? "Day Off" : row.duration || "—",
              extraPay[p.id + "-" + date] || "—",
              reasons[p.id + "-" + date] || "—",
            ]);
          });
          // Blank separator row between each person's block
          rows.push(["", "", "", "", "", "", "", "", "", ""]);
        });
    });
    const csv = rows
      .map((r) => r.map((c) => '"' + c.replace(/"/g, '""') + '"').join(","))
      .join("\n");
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `FleetOps_${startDate}_to_${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [profiles, dates, slotsByDate, extraPay, reasons]);

  const drivers = profiles.filter((p) => p.role === "driver");
  const sidemen = profiles.filter((p) => p.role === "sideman");
  const sq = search.toLowerCase();
  const matchesSearch = (p: (typeof drivers)[0]) => {
    if (!sq) return true;
    const fmtDob = (raw: string | undefined) => {
      if (!raw) return "";
      if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        const [y, m, d] = raw.split("-");
        return `${d}/${m}/${y}`;
      }
      return raw;
    };
    return (
      `${p.firstName} ${p.lastName ?? ""}`.toLowerCase().includes(sq) ||
      ((p as any).username || "").toLowerCase().includes(sq) ||
      (p.age || "").toLowerCase().includes(sq) ||
      (p.phone || "").toLowerCase().includes(sq) ||
      fmtDob(p.dob).includes(sq) ||
      (p.dob || "").includes(sq)
    );
  };
  const filteredDrivers = drivers.filter(matchesSearch);
  const filteredSidemen = sidemen.filter(matchesSearch);

  const renderTable = (profileId: string, role: "driver" | "sideman") => {
    const profile = profiles.find((p) => p.id === profileId);
    if (!profile) return null;
    const name = `${profile.firstName}${profile.lastName ? " " + profile.lastName : ""}`;
    const partnerLabel = role === "driver" ? "Sideman" : "Driver";
    const accentColor = role === "driver" ? "#E85520" : "#3ecf6e";
    const accentBg =
      role === "driver" ? "rgba(232,85,32,0.07)" : "rgba(62,207,110,0.07)";
    const accentBorder =
      role === "driver" ? "rgba(232,85,32,0.2)" : "rgba(62,207,110,0.2)";

    return (
      <div
        key={profileId}
        style={{
          marginBottom: 28,
          background: "#141518",
          border: "1px solid rgba(255,255,255,0.07)",
          borderRadius: 12,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            background: accentBg,
            borderBottom: `1px solid ${accentBorder}`,
            padding: "10px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              fontFamily: "'Barlow Condensed',sans-serif",
              fontSize: 17,
              fontWeight: 800,
              textTransform: "uppercase",
              color: "#e8e9eb",
            }}
          >
            {name}
          </div>
          <span
            style={{
              fontSize: 9,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "1px",
              padding: "3px 12px",
              borderRadius: 20,
              background: accentBg,
              border: `1px solid ${accentBorder}`,
              color: accentColor,
            }}
          >
            {role === "driver" ? "DRIVER" : "SIDEMAN"}
          </span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr
                style={{
                  background: "#0d0e10",
                  borderBottom: "2px solid rgba(255,255,255,0.08)",
                }}
              >
                {[
                  "Day",
                  "Date",
                  "Route No",
                  "User ID",
                  "Name",
                  partnerLabel,
                  "Sideman 2",
                  "Time Duration",
                  "Extra Pay",
                  "Reason for Extra Pay",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: "8px 14px",
                      textAlign: "left",
                      fontSize: 9,
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: "0.7px",
                      color: "#454850",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dates.map((date) => {
                const row = getRow(profileId, role, date) as any;
                const isEmpty = !row.off && !row.routeNo;
                return (
                  <tr
                    key={date}
                    style={{
                      borderBottom: "1px solid rgba(255,255,255,0.04)",
                      background: row.off
                        ? "rgba(100,160,255,0.04)"
                        : "transparent",
                    }}
                  >
                    <td
                      style={{
                        padding: "10px 14px",
                        color: "#e8e9eb",
                        fontWeight: 600,
                        fontSize: 13,
                      }}
                    >
                      {dayName(date)}
                    </td>
                    <td
                      style={{
                        padding: "10px 14px",
                        color: "#7a7d85",
                        fontSize: 12,
                      }}
                    >
                      {displayDate(date)}
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      {isEmpty || row.off ? (
                        <span style={{ color: "#2a2c30", fontSize: 12 }}>
                          —
                        </span>
                      ) : (
                        <span
                          style={{
                            fontFamily: "'Barlow Condensed',sans-serif",
                            fontSize: 13,
                            fontWeight: 800,
                            color: accentColor,
                          }}
                        >
                          {row.routeNo}
                        </span>
                      )}
                    </td>
                    {/* User ID */}
                    <td style={{ padding: "10px 14px" }}>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: isEmpty ? "#2a2c30" : "#7a7d85",
                          fontFamily: "monospace",
                        }}
                      >
                        {isEmpty ? "—" : (profile as any).age || "—"}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: "10px 14px",
                        color: isEmpty ? "#2a2c30" : "#e8e9eb",
                        fontWeight: 600,
                        fontSize: 13,
                      }}
                    >
                      {isEmpty ? "—" : name}
                    </td>
                    <td
                      style={{
                        padding: "10px 14px",
                        color: "#7a7d85",
                        fontSize: 13,
                      }}
                    >
                      {isEmpty || row.off ? (
                        <span style={{ color: "#2a2c30" }}>—</span>
                      ) : (
                        row.partner || "—"
                      )}
                    </td>
                    <td
                      style={{
                        padding: "10px 14px",
                        color: "#7a7d85",
                        fontSize: 13,
                      }}
                    >
                      {isEmpty || row.off ? (
                        <span style={{ color: "#2a2c30" }}>—</span>
                      ) : (
                        (row as any).partner2 || "—"
                      )}
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      {row.off ? (
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            padding: "3px 10px",
                            borderRadius: 20,
                            background: "rgba(100,160,255,0.1)",
                            border: "1px solid rgba(100,160,255,0.3)",
                            color: "#64a0ff",
                          }}
                        >
                          Day Off
                        </span>
                      ) : isEmpty ? (
                        <span style={{ color: "#2a2c30", fontSize: 12 }}>
                          —
                        </span>
                      ) : (
                        <span
                          style={{
                            color: row.duration ? "#e8e9eb" : "#454850",
                            fontWeight: 600,
                            fontSize: 13,
                          }}
                        >
                          {row.duration || "—"}
                        </span>
                      )}
                    </td>
                    {/* Extra Pay */}
                    <td style={{ padding: "6px 10px" }}>
                      <input
                        type="text"
                        value={extraPay[profileId + "-" + date] || ""}
                        onChange={(e) => {
                          saveExtraPay(profileId + "-" + date, e.target.value);
                        }}
                        placeholder="0.00"
                        style={{
                          width: 70,
                          background: "transparent",
                          border: "1px solid transparent",
                          borderRadius: 5,
                          padding: "5px 4px",
                          color: extraPay[profileId + "-" + date]
                            ? "#e8e9eb"
                            : "#7a7d85",
                          fontFamily: "'Barlow',sans-serif",
                          fontSize: 12,
                          fontWeight: 600,
                          outline: "none",
                          textAlign: "center",
                          transition: "all 0.15s",
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.background = "#1c1e22";
                          e.currentTarget.style.borderColor =
                            "rgba(255,255,255,0.12)";
                          e.currentTarget.style.color = "#e8e9eb";
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.borderColor = "transparent";
                          e.currentTarget.style.color = extraPay[
                            profileId + "-" + date
                          ]
                            ? "#e8e9eb"
                            : "#7a7d85";
                        }}
                      />
                    </td>
                    {/* Reason for Extra Pay */}
                    <td style={{ padding: "6px 10px", position: "relative" }}>
                      {reasons[profileId + "-" + date] ? (
                        <button
                          onClick={() =>
                            setOpenReason(
                              openReason === profileId + "-" + date
                                ? null
                                : profileId + "-" + date,
                            )
                          }
                          style={{
                            padding: "4px 10px",
                            background: "rgba(232,85,32,0.1)",
                            border: "1px solid rgba(232,85,32,0.3)",
                            borderRadius: 6,
                            color: "#E85520",
                            fontFamily: "'Barlow',sans-serif",
                            fontSize: 10,
                            fontWeight: 700,
                            cursor: "pointer",
                            textTransform: "uppercase",
                            whiteSpace: "nowrap",
                          }}
                        >
                          View Reason
                        </button>
                      ) : (
                        <button
                          onClick={() =>
                            setOpenReason(
                              openReason === profileId + "-" + date
                                ? null
                                : profileId + "-" + date,
                            )
                          }
                          title="Add reason"
                          style={{
                            width: 26,
                            height: 26,
                            borderRadius: 5,
                            background: "none",
                            border: "none",
                            color: "#454850",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "color 0.15s",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.color = "#E85520")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.color = "#454850")
                          }
                        >
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      )}
                      {openReason === profileId + "-" + date && (
                        <>
                          <div
                            onClick={() => setOpenReason(null)}
                            style={{ position: "fixed", inset: 0, zIndex: 29 }}
                          />
                          <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              position: "absolute",
                              top: "100%",
                              right: 0,
                              zIndex: 30,
                              background: "#1c1e22",
                              border: "1px solid rgba(232,85,32,0.4)",
                              borderRadius: 8,
                              padding: 10,
                              minWidth: 240,
                              boxShadow: "0 4px 20px rgba(0,0,0,0.6)",
                            }}
                          >
                            <div
                              style={{
                                fontSize: 9,
                                fontWeight: 700,
                                textTransform: "uppercase",
                                color: "#7a7d85",
                                marginBottom: 6,
                              }}
                            >
                              Reason for Extra Pay
                            </div>
                            <textarea
                              autoFocus
                              value={reasons[profileId + "-" + date] || ""}
                              onChange={(e) =>
                                saveReason(
                                  profileId + "-" + date,
                                  e.target.value,
                                )
                              }
                              placeholder="Enter reason…"
                              rows={3}
                              style={{
                                width: "100%",
                                background: "#141518",
                                border: "1px solid rgba(255,255,255,0.08)",
                                borderRadius: 6,
                                padding: "7px 9px",
                                color: "#e8e9eb",
                                fontFamily: "'Barlow',sans-serif",
                                fontSize: 12,
                                outline: "none",
                                resize: "none",
                                boxSizing: "border-box",
                                lineHeight: 1.5,
                              }}
                            />
                            <div
                              style={{ display: "flex", gap: 6, marginTop: 6 }}
                            >
                              {reasons[profileId + "-" + date] && (
                                <button
                                  onClick={() => {
                                    saveReason(profileId + "-" + date, "");
                                    setOpenReason(null);
                                  }}
                                  style={{
                                    flex: 1,
                                    padding: "6px",
                                    background: "transparent",
                                    border: "1px solid rgba(224,82,82,0.3)",
                                    borderRadius: 6,
                                    color: "#e05252",
                                    fontFamily: "'Barlow',sans-serif",
                                    fontSize: 11,
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    cursor: "pointer",
                                  }}
                                >
                                  Clear
                                </button>
                              )}
                              <button
                                onClick={() => setOpenReason(null)}
                                style={{
                                  flex: 2,
                                  padding: "6px",
                                  background: "#E85520",
                                  border: "none",
                                  borderRadius: 6,
                                  color: "#000",
                                  fontFamily: "'Barlow',sans-serif",
                                  fontSize: 11,
                                  fontWeight: 700,
                                  textTransform: "uppercase",
                                  cursor: "pointer",
                                }}
                              >
                                Save
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        padding: 20,
        fontFamily: "'Barlow',sans-serif",
        overflowY: "auto",
        height: "calc(100vh - 180px)",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <div
            style={{
              fontFamily: "'Barlow Condensed',sans-serif",
              fontSize: 22,
              fontWeight: 800,
              textTransform: "uppercase",
              color: "#e8e9eb",
              marginBottom: 4,
            }}
          >
            Dashboard
          </div>
          <div style={{ fontSize: 12, color: "#7a7d85" }}>
            Weekly breakdown per driver and sideman — synced live from Route
            Builder.
          </div>
        </div>
        <button
          onClick={downloadCSV}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            padding: "9px 16px",
            background: "rgba(62,207,110,0.1)",
            border: "1px solid rgba(62,207,110,0.3)",
            borderRadius: 8,
            color: "#3ecf6e",
            fontFamily: "'Barlow',sans-serif",
            fontSize: 12,
            fontWeight: 700,
            textTransform: "uppercase",
            cursor: "pointer",
            letterSpacing: "0.4px",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.background = "rgba(62,207,110,0.18)")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.background = "rgba(62,207,110,0.1)")
          }
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path
              d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Download CSV (Google Sheets)
        </button>
      </div>

      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 28,
          alignItems: "flex-end",
          background: "#141518",
          border: "1px solid rgba(255,255,255,0.07)",
          borderRadius: 10,
          padding: "14px 16px",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 9,
              fontWeight: 700,
              textTransform: "uppercase",
              color: "#7a7d85",
              marginBottom: 4,
            }}
          >
            Week
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              onClick={() => {
                setUseCustom(false);
                setWeekMonday(addDays(weekMonday, -7));
              }}
              style={{
                padding: "6px 10px",
                background: "#0d0e10",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 6,
                color: "#7a7d85",
                cursor: "pointer",
                fontSize: 14,
              }}
            >
              ‹
            </button>
            <div
              style={{
                padding: "6px 12px",
                background: "#0d0e10",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 6,
                color: "#e8e9eb",
                fontSize: 12,
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              {displayDate(weekMonday)} — {displayDate(addDays(weekMonday, 6))}
            </div>
            <button
              onClick={() => {
                setUseCustom(false);
                setWeekMonday(addDays(weekMonday, 7));
              }}
              style={{
                padding: "6px 10px",
                background: "#0d0e10",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 6,
                color: "#7a7d85",
                cursor: "pointer",
                fontSize: 14,
              }}
            >
              ›
            </button>
          </div>
        </div>
        <div>
          <div
            style={{
              fontSize: 9,
              fontWeight: 700,
              textTransform: "uppercase",
              color: "#7a7d85",
              marginBottom: 4,
            }}
          >
            Custom Range
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input
              type="date"
              value={customStart}
              onChange={(e) => {
                setCustomStart(e.target.value);
                setUseCustom(true);
              }}
              style={{
                background: "#0d0e10",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 6,
                padding: "6px 8px",
                color: "#e8e9eb",
                fontSize: 12,
                fontFamily: "'Barlow',sans-serif",
                outline: "none",
              }}
            />
            <span style={{ color: "#454850" }}>→</span>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => {
                setCustomEnd(e.target.value);
                setUseCustom(true);
              }}
              style={{
                background: "#0d0e10",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 6,
                padding: "6px 8px",
                color: "#e8e9eb",
                fontSize: 12,
                fontFamily: "'Barlow',sans-serif",
                outline: "none",
              }}
            />
            {useCustom && (
              <button
                onClick={() => {
                  setUseCustom(false);
                  setCustomStart("");
                  setCustomEnd("");
                }}
                style={{
                  padding: "5px 8px",
                  background: "transparent",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 6,
                  color: "#7a7d85",
                  cursor: "pointer",
                  fontSize: 11,
                }}
              >
                Reset
              </button>
            )}
          </div>
        </div>
        <div>
          <div
            style={{
              fontSize: 9,
              fontWeight: 700,
              textTransform: "uppercase",
              color: "#7a7d85",
              marginBottom: 4,
            }}
          >
            Search
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name, username, user ID, DOB, phone…"
            style={{
              background: "#0d0e10",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 6,
              padding: "6px 10px",
              color: "#e8e9eb",
              fontSize: 12,
              fontFamily: "'Barlow',sans-serif",
              outline: "none",
              minWidth: 180,
            }}
          />
        </div>
      </div>

      {/* Drivers */}
      {filteredDrivers.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "1px",
              color: "#E85520",
              marginBottom: 14,
              borderBottom: "1px solid rgba(232,85,32,0.2)",
              paddingBottom: 8,
            }}
          >
            Drivers
          </div>
          {filteredDrivers.map((p) => renderTable(p.id, "driver"))}
        </div>
      )}

      {/* Sidemen */}
      {filteredSidemen.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "1px",
              color: "#3ecf6e",
              marginBottom: 14,
              borderBottom: "1px solid rgba(62,207,110,0.2)",
              paddingBottom: 8,
            }}
          >
            Sidemen
          </div>
          {filteredSidemen.map((p) => renderTable(p.id, "sideman"))}
        </div>
      )}

      {filteredDrivers.length === 0 && filteredSidemen.length === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            color: "#454850",
            fontSize: 13,
          }}
        >
          No profiles match your search.
        </div>
      )}
    </div>
  );
};

export default DashboardTab;
